import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  RefreshCw, Clock, CheckCircle2, XCircle, AlertTriangle,
  DollarSign, TrendingUp, Zap, ChevronDown, ShieldAlert
} from 'lucide-react';
import { toast } from 'sonner';
import EmptyState from '@/components/EmptyState';
import RecordDocuments from '@/components/RecordDocuments';
import { RenewalBindGates } from '@/components/RenewalBindGates';
import { Paperclip } from 'lucide-react';
import { useSort, SortableTh } from '@/components/SortableTh';

const RC = { navy: '#0D1B2A', blue: '#1A4C7C', slate: '#5C6B7A', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020' };

function RepricingImpactCard({ api }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get('/renewals/repricing-impact').then(r => setData(r.data)).catch(() => {});
  }, [api]);

  if (!data || data.movers.length === 0) return null;
  return (
    <Card className="rounded-sm bg-white" style={{ border: '1px solid rgba(232,160,32,0.4)' }} data-testid="repricing-impact-card">
      <button onClick={() => setOpen(v => !v)} className="w-full flex flex-wrap items-center gap-3 px-4 py-3 text-left" data-testid="repricing-impact-toggle">
        <TrendingUp className="w-4 h-4" style={{ color: RC.amber }} />
        <span className="text-sm font-semibold" style={{ color: RC.navy }}>
          Repricing impact — {data.movers.length} renewal quote{data.movers.length !== 1 ? 's' : ''} move tier under code-based occupation pricing
        </span>
        <span className="text-xs" style={{ color: RC.slate }}>
          {data.moving_up} up · {data.moving_down} down · {data.unchanged} unchanged of {data.reviewed} reviewed
        </span>
        <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${open ? 'rotate-180' : ''}`} style={{ color: RC.slate }} />
      </button>
      {open && (
        <div className="border-t max-h-[320px] overflow-y-auto" style={{ borderColor: 'rgba(136,150,168,0.15)' }}>
          <table className="w-full text-xs">
            <thead><tr className="border-b" style={{ background: '#FAFBFC', borderColor: 'rgba(136,150,168,0.1)' }}>
              {['Renewal', 'Insured', 'Occupation → Code', 'Tier', 'Premium impact', 'Current → Est.', 'Status'].map(h => (
                <th key={h} className="text-left py-2 px-3 text-[10px] uppercase tracking-wider font-medium" style={{ color: RC.slate }}>{h}</th>))}
            </tr></thead>
            <tbody>
              {data.movers.map(m => (
                <tr key={m.renewal_id} className="border-b" style={{ borderColor: 'rgba(136,150,168,0.08)' }} data-testid={`repricing-row-${m.renewal_reference}`}>
                  <td className="py-2 px-3 font-mono" style={{ color: RC.navy }}>{m.renewal_reference}</td>
                  <td className="py-2 px-3 max-w-[170px] truncate" style={{ color: RC.navy }}>{m.business_name}</td>
                  <td className="py-2 px-3" style={{ color: RC.slate }}>{m.industry} → <span className="font-mono">{m.anzsic_code}</span></td>
                  <td className="py-2 px-3 font-semibold" style={{ color: m.new_tier > m.old_tier ? RC.red : RC.green }}>{m.old_tier} → {m.new_tier}</td>
                  <td className="py-2 px-3 font-semibold" style={{ color: m.now_refer ? RC.red : (m.premium_impact_pct || 0) > 0 ? RC.amber : RC.green }}>
                    {m.now_refer ? 'NOW REFER' : `${m.premium_impact_pct > 0 ? '+' : ''}${m.premium_impact_pct}%`}
                  </td>
                  <td className="py-2 px-3" style={{ color: RC.slate }}>
                    ${Math.round(m.renewal_premium || 0).toLocaleString()}{m.est_new_premium ? ` → $${Math.round(m.est_new_premium).toLocaleString()}` : ''}
                  </td>
                  <td className="py-2 px-3 capitalize" style={{ color: RC.slate }}>{(m.status || '').replace(/_/g, ' ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-4 py-2 text-[10px]" style={{ color: RC.slate }}>Estimates apply the occupation-loading ratio to the current renewal premium. Review before the quote goes out — regenerating the quote applies the new tier.</p>
        </div>
      )}
    </Card>
  );
}

const C = {
  navy: '#0D1B2A', saffron: '#FF9933', teal: '#00B4D8', green: '#1D9E75',
  red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', bg: '#F5F7FA', blue: '#1A4C7C',
};

function fmt$(v) {
  if (!v) return '$0';
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v.toLocaleString()}`;
}

const STATUS_CONFIG = {
  identified: { color: C.slate, label: 'Identified', icon: Clock },
  quote_generated: { color: C.teal, label: 'Quoted', icon: RefreshCw },
  held_for_review: { color: C.amber, label: 'Held', icon: AlertTriangle },
  offered: { color: C.blue, label: 'Offered', icon: TrendingUp },
  bind_blocked: { color: C.red, label: 'Bind blocked', icon: ShieldAlert },
  accepted: { color: C.green, label: 'Accepted', icon: CheckCircle2 },
  declined: { color: C.red, label: 'Declined', icon: XCircle },
  lapsed: { color: C.slate, label: 'Lapsed', icon: AlertTriangle },
};

export default function RenewalsPage() {
  const api = useApi();
  const [pipeline, setPipeline] = useState(null);
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [filter, setFilter] = useState('');
  const [docsFor, setDocsFor] = useState(null);
  const [gatesFor, setGatesFor] = useState(null);
  const [highlightId, setHighlightId] = useState(null);

  // Deep link from digest email: /renewals?renewal={id}
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('renewal');
    if (id) setHighlightId(id);
  }, []);
  useEffect(() => {
    if (!highlightId || !renewals.length) return;
    const el = document.getElementById(`renewal-row-${highlightId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightId, renewals]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(() => {
    Promise.all([
      api.get('/renewals/pipeline'),
      api.get(filter ? `/renewals?limit=100&status=${filter}` : '/renewals?limit=100'),
    ]).then(([p, r]) => { setPipeline(p.data); setRenewals(r.data); }).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api, filter]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const handleBatchGenerate = async () => {
    setGenerating(true);
    await api.post('/renewals/batch?days=365&adjustment_pct=3').catch(err => { console.error('API error:', err?.message); });
    setGenerating(false);
    load();
  };

  const handleAction = async (renewalId, action) => {
    try {
      await api.put(`/renewals/${renewalId}/action`, { action });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Action failed';
      toast.error(detail);
      if (err?.response?.status === 409) setGatesFor(renewalId);
    }
    load();
  };

  const filtered = filter ? renewals.filter(r => r.status === filter) : renewals;
  const sort = useSort(filtered, 'days_to_expiry', 'asc');

  if (loading) return <div className="space-y-3" data-testid="renewals-loading">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 rounded-sm" />)}</div>;

  return (
    <div className="space-y-5" data-testid="renewals-page">
      <RepricingImpactCard api={api} />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: C.navy, fontFamily: "'IBM Plex Sans', sans-serif" }}>Renewal Pipeline</h1>
          <p className="text-sm mt-1" style={{ color: C.slate }}>Automated policy renewal workflow — ARIA-OPS-REN</p>
        </div>
        <button onClick={handleBatchGenerate} disabled={generating}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-sm text-sm font-medium text-white disabled:opacity-50"
          style={{ background: C.blue }} data-testid="batch-generate-btn">
          <Zap className="w-4 h-4" /> {generating ? 'Generating...' : 'Generate Renewal Quotes'}
        </button>
      </div>

      {/* Pipeline KPIs */}
      {pipeline && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <MiniKPI label="Total Renewals" value={pipeline.total_renewals} color={C.blue} />
          <MiniKPI label="Pending Candidates" value={pipeline.pending_candidates} color={C.amber} />
          <MiniKPI label="Quoted" value={pipeline.pipeline.quote_generated?.count || 0} color={C.teal} />
          <MiniKPI label="Offered" value={pipeline.pipeline.offered?.count || 0} color={C.blue} />
          <MiniKPI label="Accepted" value={pipeline.pipeline.accepted?.count || 0} color={C.green} />
          <MiniKPI label="Retention" value={`${pipeline.retention_rate}%`} color={pipeline.retention_rate >= 80 ? C.green : C.amber} />
        </div>
      )}

      {/* Pipeline Funnel */}
      {pipeline && pipeline.total_renewals > 0 && (
        <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
          <h3 className="text-sm font-semibold mb-3 uppercase tracking-wider" style={{ color: C.navy }}>Pipeline Funnel</h3>
          <div className="flex gap-2">
            {Object.entries(pipeline.pipeline).map(([status, data]) => {
              const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.identified;
              const pct = pipeline.total_renewals > 0 ? Math.max(5, (data.count / pipeline.total_renewals) * 100) : 0;
              return (
                <button key={status} onClick={() => setFilter(filter === status ? '' : status)}
                  className={`flex-1 p-2 rounded-sm text-center transition-all ${filter === status ? 'ring-2' : ''}`}
                  style={{ background: cfg.color + '10', ringColor: cfg.color }}>
                  <p className="text-lg font-semibold" style={{ color: cfg.color }}>{data.count}</p>
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: C.slate }}>{cfg.label}</p>
                  <p className="text-[10px] font-mono" style={{ color: C.slate }}>{fmt$(data.premium)}</p>
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {/* Filter */}
      <div className="flex gap-2 items-center">
        <select value={filter} onChange={e => setFilter(e.target.value)}
          className="px-3 py-2 text-sm border rounded-sm" style={{ color: C.navy }} data-testid="renewal-filter">
          <option value="">All Statuses</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <span className="text-sm" style={{ color: C.slate }}>{filtered.length} renewals</span>
      </div>

      {/* Renewals Table */}
      <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="renewals-table">
            <thead>
              <tr style={{ background: C.bg }} className="border-b border-[rgba(136,150,168,0.15)]">
                <SortableTh label="Reference" sortKey="renewal_reference" sort={sort} />
                <SortableTh label="Business" sortKey="business_name" sort={sort} />
                <SortableTh label="Class" sortKey="insurance_class" sort={sort} />
                <SortableTh label="Current" sortKey="current_premium" sort={sort} />
                <SortableTh label="Renewal" sortKey="renewal_premium" sort={sort} />
                <SortableTh label="Change" sortKey="premium_change_pct" sort={sort} />
                <SortableTh label="Expiry" sortKey="days_to_expiry" sort={sort} />
                <SortableTh label="Status" sortKey="status" sort={sort} />
                {['Docs', 'Actions'].map(h => (
                  <th key={h} className="text-left py-2 px-3 text-[11px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={10} className="p-0">
                  {renewals.length === 0 ? <EmptyState type="renewals" /> : <EmptyState type="search" compact />}
                </td></tr>
              ) : sort.sorted.map(r => {
                const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.identified;
                return (
                  <React.Fragment key={r.renewal_id}>
                  <tr id={`renewal-row-${r.renewal_id}`} className="border-b border-[rgba(136,150,168,0.08)] hover:bg-[#F5F7FA]"
                    style={highlightId === r.renewal_id ? { background: '#FDF7EC', boxShadow: 'inset 3px 0 0 #E8A020' } : undefined}
                    data-testid={`renewal-row-${r.renewal_id}`}>
                    <td className="py-2 px-3 font-mono text-xs" style={{ color: C.blue }}>
                      {r.renewal_reference}
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/s58-renewal/${r.renewal_id}`} target="_blank" rel="noreferrer"
                        data-testid={`s58-letter-${r.renewal_id}`} className="ml-1.5 text-[9px] font-semibold hover:underline" style={{ color: '#A8720E' }}
                        title="s58 renewal notification letter (PDF)">s58</a>
                    </td>
                    <td className="py-2 px-3 text-xs font-medium max-w-[180px]" style={{ color: C.navy }}>
                      <span className="truncate block">{r.business_name}</span>
                      {r.status === 'held_for_review' && r.hold_reason && (
                        <span className="block text-[9px] mt-0.5 truncate" style={{ color: '#B27516' }} title={r.hold_reason} data-testid={`hold-reason-${r.renewal_id}`}>{r.hold_reason}</span>
                      )}
                      {r.status === 'held_for_review' && r.ai_review && (
                        <span className="block text-[9px] mt-0.5 truncate" style={{ color: '#0E7490' }} title={r.ai_review.reasoning} data-testid={`ai-review-${r.renewal_id}`}>
                          <b>AI {r.ai_review.decision === 'escalate' ? 'escalated to human' : r.ai_review.decision}:</b> {r.ai_review.reasoning}
                        </span>
                      )}
                      {r.status === 'bind_blocked' && (
                        <span className="block text-[9px] mt-0.5" style={{ color: '#B03230' }} data-testid={`bind-blocked-reason-${r.renewal_id}`}
                          title={(r.bind_blocked_reasons || []).join(' ')}>
                          <b>Bind blocked:</b> {(r.bind_blocked_reasons || ['a bind gate is unresolved'])[0].slice(0, 90)}…
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-xs" style={{ color: C.slate }}>{r.insurance_class}</td>
                    <td className="py-2 px-3 text-xs font-mono" style={{ color: C.navy }}>{fmt$(r.current_premium)}</td>
                    <td className="py-2 px-3 text-xs font-mono font-medium" style={{ color: C.navy }}>{fmt$(r.renewal_premium)}</td>
                    <td className="py-2 px-3">
                      <span className="text-xs font-mono" style={{ color: r.premium_change_pct > 0 ? C.amber : C.green }}>
                        {r.premium_change_pct > 0 ? '+' : ''}{r.premium_change_pct}%
                      </span>
                    </td>
                    <td className="py-2 px-3">
                      <span className="text-xs" style={{ color: r.days_to_expiry <= 14 ? C.red : r.days_to_expiry <= 30 ? C.amber : C.slate }}>
                        {r.days_to_expiry}d
                      </span>
                    </td>
                    <td className="py-2 px-3">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: cfg.color + '15', color: cfg.color }}>{cfg.label}</span>
                    </td>
                    <td className="py-2 px-3">
                      <button onClick={() => setDocsFor(docsFor === r.renewal_id ? null : r.renewal_id)}
                        className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-colors hover:opacity-80"
                        style={{ background: docsFor === r.renewal_id ? 'rgba(26,76,124,0.15)' : 'rgba(26,76,124,0.06)', color: C.blue }}
                        data-testid={`renewal-docs-btn-${r.renewal_id}`}>
                        <Paperclip className="w-3 h-3" />Docs
                      </button>
                    </td>
                    <td className="py-2 px-3">
                      {r.status === 'held_for_review' && (
                        <button onClick={() => handleAction(r.renewal_id, 'release')}
                          className="px-2 py-1 rounded text-[10px] font-medium text-white" style={{ background: C.amber }}
                          data-testid={`release-${r.renewal_id}`}>Release</button>
                      )}
                      {r.status === 'quote_generated' && (
                        <button onClick={() => handleAction(r.renewal_id, 'offer')}
                          className="px-2 py-1 rounded text-[10px] font-medium text-white" style={{ background: C.blue }}
                          data-testid={`offer-${r.renewal_id}`}>Offer</button>
                      )}
                      {(r.status === 'offered' || r.status === 'bind_blocked') && (
                        <div className="flex gap-1">
                          <button onClick={() => handleAction(r.renewal_id, 'accept')}
                            className="px-2 py-1 rounded text-[10px] font-medium text-white" style={{ background: C.green }}
                            data-testid={`accept-${r.renewal_id}`}>{r.status === 'bind_blocked' ? 'Retry bind' : 'Accept'}</button>
                          <button onClick={() => setGatesFor(gatesFor === r.renewal_id ? null : r.renewal_id)}
                            className="px-2 py-1 rounded text-[10px] font-medium" style={{ background: 'rgba(226,75,74,0.08)', color: C.red }}
                            data-testid={`gates-btn-${r.renewal_id}`}>Gates</button>
                          {r.status === 'offered' && (
                            <button onClick={() => handleAction(r.renewal_id, 'decline')}
                              className="px-2 py-1 rounded text-[10px] font-medium text-white" style={{ background: C.red }}>Decline</button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                  {gatesFor === r.renewal_id && (
                    <tr><td colSpan={10} className="px-6 py-3" style={{ background: '#FDF6F6' }}>
                      <RenewalBindGates renewalId={r.renewal_id} onChanged={() => { setGatesFor(null); load(); }} />
                    </td></tr>
                  )}
                  {docsFor === r.renewal_id && (
                    <tr><td colSpan={10} className="px-6 py-3" style={{ background: '#FAFBFC' }}>
                      <RecordDocuments recordType="renewal" recordId={r.renewal_id} />
                    </td></tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function MiniKPI({ label, value, color }) {
  return (
    <Card className="p-3 rounded-sm border border-[rgba(136,150,168,0.15)] bg-white">
      <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{label}</p>
      <p className="text-base font-semibold mt-0.5" style={{ color: color || C.navy }}>{value}</p>
    </Card>
  );
}
